import { lazy, Suspense, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { MapLocationsResponse, MapPersonaKey } from '@truecare/schemas';
import { MAP_PERSONA_KEYS } from '@truecare/schemas';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { adminBeforeLoad } from '../lib/admin-before-load';
import { MAP_PERSONAS, PersonaMarkerIcon } from '../lib/map-personas';
import { filterPoints } from '../lib/map-clusters';

// Lazy so the ~1.8MB mapbox-gl bundle only downloads when the operator opens the
// map, not on every admin page.
const LocationMap = lazy(() =>
  import('../components/LocationMap').then((m) => ({ default: m.LocationMap })),
);

/**
 * Operator location map. Shows where Care Professionals, Facilities, and Homecare
 * recipients are, with persona/status/role filters. Filtering is client-side (v1
 * pilot scale); ungeocoded entities are excluded server-side. Needs a public
 * Mapbox token (VITE_MAPBOX_PUBLIC_TOKEN); degrades to a notice if absent.
 */
export const Route = createFileRoute('/map')({
  beforeLoad: () => adminBeforeLoad('/map'),
  component: MapPage,
});

function toggle<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

const MAPBOX_TOKEN =
  (import.meta.env['VITE_MAPBOX_PUBLIC_TOKEN'] as string | undefined) ??
  (import.meta.env['PUBLIC_MAPBOX_PUBLIC_TOKEN'] as string | undefined);

function MapPage() {
  const query = useQuery<MapLocationsResponse>({
    queryKey: ['admin', 'map', 'locations'],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/map/locations' as never),
      )) as unknown as MapLocationsResponse,
  });

  const points = useMemo(() => query.data?.points ?? [], [query.data]);

  const [activePersonas, setActivePersonas] = useState<Set<MapPersonaKey>>(
    () => new Set(MAP_PERSONA_KEYS),
  );
  const [statuses, setStatuses] = useState<Set<string>>(new Set());
  const [roles, setRoles] = useState<Set<string>>(new Set());

  const filtered = useMemo(
    () => filterPoints(points, { personas: activePersonas, statuses, roles }),
    [points, activePersonas, statuses, roles],
  );

  const countByPersona = useMemo(() => {
    const m = new Map<MapPersonaKey, number>();
    for (const p of points) m.set(p.persona, (m.get(p.persona) ?? 0) + 1);
    return m;
  }, [points]);

  // Status options come from the points of the currently-active personas.
  const availableStatuses = useMemo(() => {
    const s = new Set<string>();
    for (const p of points) if (activePersonas.has(p.persona)) s.add(p.status);
    return [...s].sort();
  }, [points, activePersonas]);

  const availableRoles = useMemo(() => {
    const s = new Set<string>();
    for (const p of points) for (const r of p.roles) s.add(r);
    return [...s].sort();
  }, [points]);

  return (
    <AdminLayout>
      <header className="mb-4">
        <h1 className="font-heading text-3xl text-forest md:text-4xl">Service map</h1>
        <p className="font-body text-sm text-ink-muted">
          Where Care Professionals, Facilities, and Homecare recipients are.
          Ungeocoded entities aren&apos;t shown (see the &ldquo;Unmatchable (no
          location)&rdquo; dashboard metric). Operator-only.
        </p>
      </header>

      {!MAPBOX_TOKEN ? (
        <div
          data-testid="map-no-token"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          Map is not configured — set <code>VITE_MAPBOX_PUBLIC_TOKEN</code> for the
          admin app to display it.
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
          {/* Filters + legend */}
          <aside className="space-y-5 text-sm">
            <fieldset>
              <legend className="mb-2 font-heading text-forest">Show</legend>
              <div className="space-y-2">
                {MAP_PERSONAS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={activePersonas.has(p.key)}
                      onChange={() => setActivePersonas((s) => toggle(s, p.key))}
                    />
                    <PersonaMarkerIcon shape={p.shape} color={p.color} size={18} />
                    <span>{p.label}</span>
                    <span className="ml-auto text-ink-muted">
                      {countByPersona.get(p.key) ?? 0}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {availableStatuses.length > 0 && (
              <fieldset>
                <legend className="mb-2 font-heading text-forest">Status</legend>
                <div className="space-y-1">
                  {availableStatuses.map((s) => (
                    <label key={s} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={statuses.has(s)}
                        onChange={() => setStatuses((prev) => toggle(prev, s))}
                      />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-ink-muted">None ticked = all.</p>
              </fieldset>
            )}

            {availableRoles.length > 0 && (
              <fieldset>
                <legend className="mb-2 font-heading text-forest">
                  Professional role
                </legend>
                <div className="space-y-1">
                  {availableRoles.map((r) => (
                    <label key={r} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={roles.has(r)}
                        onChange={() => setRoles((prev) => toggle(prev, r))}
                      />
                      <span>{r}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  Filters Professionals only.
                </p>
              </fieldset>
            )}

            <p className="text-xs text-ink-muted" data-testid="map-point-count">
              {query.isPending
                ? 'Loading…'
                : query.isError
                  ? 'Failed to load locations.'
                  : `${filtered.length} of ${points.length} shown`}
            </p>
          </aside>

          {/* Map */}
          <div className="h-[70vh] overflow-hidden rounded-lg border border-sand-300">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center text-sm text-ink-muted">
                  Loading map…
                </div>
              }
            >
              <LocationMap
                points={filtered}
                activePersonas={activePersonas}
                token={MAPBOX_TOKEN}
              />
            </Suspense>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
